import { MODEL_METADATA, LLM_PROVIDERS } from "@pick-three-ai/constants";
import type { LLMModel } from "@pick-three-ai/constants";
import { ProviderIcon } from "./ProviderIcon";
import { useAppMode } from "../hooks";

// Pre-defined sample rankings for AI mode
const AI_SAMPLE_RANKINGS: Array<{
  models: [LLMModel, LLMModel, LLMModel];
  blurbs: [string, string, string];
  handle: string;
  title: string;
}> = [
  {
    models: ["Claude Opus 4.5", "GPT-4o", "Gemini 2.5 Pro"],
    blurbs: ["Deep reasoning king", "Fast & reliable", "Great at code"],
    handle: "dev",
    title: "Best for Coding",
  },
  {
    models: ["GPT-5", "Claude Sonnet 4", "Grok 3"],
    blurbs: ["Next level", "Perfect balance", "Unfiltered takes"],
    handle: "sam",
    title: "Daily Drivers",
  },
  {
    models: ["Gemini 2.5 Pro", "Claude 3.5 Sonnet", "Llama 4 405B"],
    blurbs: ["Multimodal beast", "So reliable", "Open source FTW"],
    handle: "ai",
    title: "Best Overall",
  },
  {
    models: ["DeepSeek V3", "Qwen 2.5", "Mistral Large"],
    blurbs: ["Underrated gem", "Surprising quality", "Fast inference"],
    handle: "max",
    title: "Hidden Gems",
  },
  {
    models: ["o3", "Claude Opus 4", "Gemini Ultra"],
    blurbs: ["Reasoning god", "Long context king", "Massive context"],
    handle: "pro",
    title: "For Hard Tasks",
  },
  {
    models: ["GPT-4 Turbo", "Claude 3 Opus", "Llama 3.3 70B"],
    blurbs: ["Still great", "Classic quality", "Local hero"],
    handle: "joe",
    title: "Tried & True",
  },
];

// Pre-defined sample rankings for generic mode
const GENERIC_SAMPLE_RANKINGS: Array<{
  entries: [string, string, string];
  blurbs: [string, string, string];
  handle: string;
  title: string;
}> = [
  {
    entries: ["Pepperoni", "Margherita", "Hawaiian"],
    blurbs: ["Classic forever", "Simple perfection", "Fight me"],
    handle: "joe",
    title: "Best Pizza",
  },
  {
    entries: ["The Godfather", "Inception", "Parasite"],
    blurbs: ["Timeless masterpiece", "Mind-bending", "Perfect film"],
    handle: "sam",
    title: "Top Movies",
  },
  {
    entries: ["Tokyo", "Barcelona", "New York"],
    blurbs: ["Food paradise", "Architecture dreams", "Never sleeps"],
    handle: "ava",
    title: "Dream Cities",
  },
  {
    entries: ["Coffee", "Tea", "Water"],
    blurbs: ["Morning fuel", "Afternoon calm", "Stay hydrated"],
    handle: "max",
    title: "Best Drinks",
  },
  {
    entries: ["Dog", "Cat", "Capybara"],
    blurbs: ["Loyal friend", "Independent vibes", "So chill"],
    handle: "pet",
    title: "Best Pets",
  },
  {
    entries: ["Spring", "Fall", "Summer"],
    blurbs: ["Perfect weather", "Cozy vibes", "Beach time"],
    handle: "sky",
    title: "Fav Season",
  },
];

// Simplified rank styles for background cards (no expensive effects)
const RANK_STYLES = {
  1: {
    badgeClass: "rank-badge-1",
    label: "1st",
  },
  2: {
    badgeClass: "rank-badge-2",
    label: "2nd",
  },
  3: {
    badgeClass: "rank-badge-3",
    label: "3rd",
  },
} as const;

// Mini slot component for AI mode - simplified for background (no blur/glow effects)
function AIMiniSlot({
  rank,
  model,
  blurb,
}: {
  rank: 1 | 2 | 3;
  model: LLMModel;
  blurb: string;
}) {
  const styles = RANK_STYLES[rank];
  const provider = MODEL_METADATA[model].provider;
  const providerInfo = LLM_PROVIDERS[provider];
  const isHero = rank === 1;

  return (
    <div
      className={`
        rounded-lg bg-white/5 border border-white/10
        ${isHero ? "px-2 py-2" : "px-1.5 py-1.5"}
      `}
    >
      <div className="flex items-center gap-1.5">
        {/* Metallic Badge */}
        <div
          className={`
            flex-shrink-0 flex items-center justify-center 
            rounded
            font-bold
            ${styles.badgeClass}
            ${isHero ? "w-6 h-6 text-[8px]" : "w-5 h-5 text-[7px]"}
          `}
        >
          {styles.label}
        </div>

        {/* Provider Icon */}
        <div className="flex-shrink-0 opacity-80">
          <ProviderIcon provider={provider} size={isHero ? 16 : 14} />
        </div>

        {/* Model Name */}
        <div className="flex-1 min-w-0">
          <div
            className={`font-bold text-white/90 truncate ${isHero ? "text-[10px]" : "text-[9px]"}`}
          >
            {model}
          </div>
          <div
            className={`text-white/50 truncate uppercase tracking-wider font-medium ${isHero ? "text-[7px]" : "text-[6px]"}`}
          >
            {providerInfo.name}
          </div>
        </div>
      </div>

      {/* Blurb */}
      <p
        className={`italic text-white/60 truncate mt-1 ${isHero ? "text-[8px] ml-[30px]" : "text-[7px] ml-[26px]"}`}
      >
        "{blurb}"
      </p>
    </div>
  );
}

// Mini slot component for generic mode - simplified (no provider icons)
function GenericMiniSlot({
  rank,
  entry,
  blurb,
}: {
  rank: 1 | 2 | 3;
  entry: string;
  blurb: string;
}) {
  const styles = RANK_STYLES[rank];
  const isHero = rank === 1;

  return (
    <div
      className={`
        rounded-lg bg-white/5 border border-white/10
        ${isHero ? "px-2 py-2" : "px-1.5 py-1.5"}
      `}
    >
      <div className="flex items-center gap-1.5">
        {/* Metallic Badge */}
        <div
          className={`
            flex-shrink-0 flex items-center justify-center 
            rounded
            font-bold
            ${styles.badgeClass}
            ${isHero ? "w-6 h-6 text-[8px]" : "w-5 h-5 text-[7px]"}
          `}
        >
          {styles.label}
        </div>

        {/* Entry Name */}
        <div className="flex-1 min-w-0">
          <div
            className={`font-bold text-white/90 truncate ${isHero ? "text-[10px]" : "text-[9px]"}`}
          >
            {entry}
          </div>
        </div>
      </div>

      {/* Blurb */}
      <p
        className={`italic text-white/60 truncate mt-1 ${isHero ? "text-[8px] ml-[30px]" : "text-[7px] ml-[26px]"}`}
      >
        "{blurb}"
      </p>
    </div>
  );
}

// Mini card component for AI mode - simplified for background (no blur effects)
function AIMiniCard({
  ranking,
  opacity,
}: {
  ranking: (typeof AI_SAMPLE_RANKINGS)[0];
  opacity: number;
}) {
  return (
    <div
      className="w-[200px] sm:w-[240px] shrink-0 rounded-xl bg-white/[0.04] border border-white/10 p-2.5"
      style={{ opacity }}
    >
      {/* Header matching real card */}
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-white/10">
        <span className="text-[8px] text-white/50 font-medium">Dec 2025</span>
        <span className="text-[9px] text-white/60 font-bold truncate max-w-[45%]">
          {ranking.title}
        </span>
        <span className="text-[8px] text-white/50">@{ranking.handle}</span>
      </div>

      {/* Ranking slots with size hierarchy */}
      <div className="flex flex-col">
        {/* Hero slot (1st place) - larger */}
        <div className="mb-1.5">
          <AIMiniSlot rank={1} model={ranking.models[0]} blurb={ranking.blurbs[0]} />
        </div>

        {/* 2nd & 3rd - slightly narrower like real card */}
        <div className="space-y-1 mx-1">
          <AIMiniSlot rank={2} model={ranking.models[1]} blurb={ranking.blurbs[1]} />
          <AIMiniSlot rank={3} model={ranking.models[2]} blurb={ranking.blurbs[2]} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-2 pt-1.5 border-t border-white/10 text-center">
        <span className="text-[7px] text-white/40 font-medium tracking-wide">
          Make yours at pickthree.ai
        </span>
      </div>
    </div>
  );
}

// Mini card component for generic mode - simplified for background (no blur effects)
function GenericMiniCard({
  ranking,
  opacity,
}: {
  ranking: (typeof GENERIC_SAMPLE_RANKINGS)[0];
  opacity: number;
}) {
  return (
    <div
      className="w-[200px] sm:w-[240px] shrink-0 rounded-xl bg-white/[0.04] border border-white/10 p-2.5"
      style={{ opacity }}
    >
      {/* Header matching real card */}
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-white/10">
        <span className="text-[8px] text-white/50 font-medium">Dec 2025</span>
        <span className="text-[9px] text-white/60 font-bold truncate max-w-[45%]">
          {ranking.title}
        </span>
        <span className="text-[8px] text-white/50">@{ranking.handle}</span>
      </div>

      {/* Ranking slots with size hierarchy */}
      <div className="flex flex-col">
        {/* Hero slot (1st place) - larger */}
        <div className="mb-1.5">
          <GenericMiniSlot rank={1} entry={ranking.entries[0]} blurb={ranking.blurbs[0]} />
        </div>

        {/* 2nd & 3rd - slightly narrower like real card */}
        <div className="space-y-1 mx-1">
          <GenericMiniSlot rank={2} entry={ranking.entries[1]} blurb={ranking.blurbs[1]} />
          <GenericMiniSlot rank={3} entry={ranking.entries[2]} blurb={ranking.blurbs[2]} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-2 pt-1.5 border-t border-white/10 text-center">
        <span className="text-[7px] text-white/40 font-medium tracking-wide">
          Make yours at pickthree.me
        </span>
      </div>
    </div>
  );
}

// Pre-computed opacities for deterministic rendering (slightly dimmed)
const CARD_OPACITIES = [0.32, 0.38, 0.28, 0.35, 0.30, 0.40];

export function BackgroundCardGallery() {
  const { isGeneric } = useAppMode();

  // 3 rows with brick pattern offset (middle row shifted by half card width)
  // Each row has cards duplicated for seamless infinite scroll
  // Card width ~220px + gap 32px = ~252px per unit, half = ~126px
  const rows = [
    { cards: [0, 1, 2, 3, 4, 5], offsetX: -80, speed: 100 },
    { cards: [3, 4, 5, 0, 1, 2], offsetX: -80 - 0, speed: 100 }, // Offset by half card for brick pattern
    { cards: [1, 5, 3, 2, 4, 0], offsetX: -80, speed: 100 },
  ];

  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none select-none"
      style={{ zIndex: -1 }}
      aria-hidden="true"
      data-background-gallery="true"
    >
      {/* The grid of cards */}
      <div className="absolute inset-0 flex flex-col justify-center gap-12 py-8">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex gap-8 animate-drift-infinite"
            style={{
              marginLeft: row.offsetX,
              animationDelay: `${rowIndex * -15}s`,
              animationDuration: `${row.speed}s`,
            }}
          >
            {/* First set of cards */}
            {row.cards.map((cardIndex, i) => {
              const opacity = CARD_OPACITIES[(cardIndex + i) % CARD_OPACITIES.length];
              if (opacity === undefined) return null;
              
              if (isGeneric) {
                const ranking = GENERIC_SAMPLE_RANKINGS[cardIndex];
                if (!ranking) return null;
                return (
                  <GenericMiniCard
                    key={`${rowIndex}-a-${i}`}
                    ranking={ranking}
                    opacity={opacity}
                  />
                );
              } else {
                const ranking = AI_SAMPLE_RANKINGS[cardIndex];
                if (!ranking) return null;
                return (
                  <AIMiniCard
                    key={`${rowIndex}-a-${i}`}
                    ranking={ranking}
                    opacity={opacity}
                  />
                );
              }
            })}
            {/* Duplicate set for seamless loop */}
            {row.cards.map((cardIndex, i) => {
              const opacity = CARD_OPACITIES[(cardIndex + i) % CARD_OPACITIES.length];
              if (opacity === undefined) return null;
              
              if (isGeneric) {
                const ranking = GENERIC_SAMPLE_RANKINGS[cardIndex];
                if (!ranking) return null;
                return (
                  <GenericMiniCard
                    key={`${rowIndex}-b-${i}`}
                    ranking={ranking}
                    opacity={opacity}
                  />
                );
              } else {
                const ranking = AI_SAMPLE_RANKINGS[cardIndex];
                if (!ranking) return null;
                return (
                  <AIMiniCard
                    key={`${rowIndex}-b-${i}`}
                    ranking={ranking}
                    opacity={opacity}
                  />
                );
              }
            })}
          </div>
        ))}
      </div>

      {/* Heavy radial fade - cards visible only at edges */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(
              ellipse 50% 80% at 50% 50%,
              var(--color-background) 0%,
              var(--color-background) 41%,
              transparent 100%
            )
          `,
        }}
      />

      {/* Extra fade for top/bottom edges */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(to bottom,
              var(--color-background) 0%,
              transparent 15%,
              transparent 85%,
              var(--color-background) 100%
            )
          `,
        }}
      />
    </div>
  );
}
